import { useMemo, useState } from "react";
import { AUDIT_FIELDS } from "../data/fields";

/** Human label for a coded field, falling back to the raw key so a field the
 *  UI does not know yet renders as its key rather than vanishing. */
export function fieldLabel(key: string): string {
  return AUDIT_FIELDS.find((f) => f.key === key)?.label ?? key;
}

export type SideFilter = "all" | "agent" | "coder";

/** Filter state shared by both code tables: field chips, a code-prefix search,
 *  and (errored table only) the side judged wrong. Chip counts are computed
 *  over the OTHER filters, so a chip never promises rows the search hides. */
export function useCodeFilter<T extends { field: string; code: string }>(
  rows: T[],
  sideOf?: (r: T) => { agent: boolean; coder: boolean },
) {
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [side, setSide] = useState<SideFilter>("all");

  const categories = useMemo(() => {
    const seen: string[] = [];
    for (const r of rows) if (!seen.includes(r.field)) seen.push(r.field);
    return seen;
  }, [rows]);
  // A category that disappears between loads must not strand the filter on
  // it, or the table reads as empty when it is merely filtered to nothing.
  const active = category !== "all" && !categories.includes(category) ? "all" : category;

  const needle = search.trim().toLowerCase();

  const { shown, counts, total } = useMemo(() => {
    const counts: Record<string, number> = {};
    let total = 0;
    const shown: T[] = [];
    for (const r of rows) {
      const passesOtherFilters =
        (needle === "" || r.code.toLowerCase().startsWith(needle)) &&
        (side === "all" || !sideOf || sideOf(r)[side]);
      if (!passesOtherFilters) continue;
      counts[r.field] = (counts[r.field] ?? 0) + 1;
      total++;
      if (active === "all" || r.field === active) shown.push(r);
    }
    return { shown, counts, total };
  }, [rows, active, needle, side, sideOf]);

  return { shown, categories, counts, total, category: active, setCategory, search, setSearch, side, setSide };
}

const chipClass = (on: boolean) =>
  "rounded-coronis border px-2.5 py-1 text-[12px] font-medium transition-colors " +
  (on ? "border-transparent bg-foreground text-background" : "border-border bg-card text-muted-foreground hover:bg-muted");

export function CodeTableToolbar({ categories, counts, total, category, onCategory, search, onSearch, side, onSide }: {
  categories: string[]; counts: Record<string, number>; total: number;
  category: string; onCategory: (c: string) => void;
  search: string; onSearch: (s: string) => void;
  side?: SideFilter; onSide?: (s: SideFilter) => void;
}) {
  const chip = (key: string, label: string, n: number, on: boolean, onClick: () => void) => (
    <button key={key} type="button" onClick={onClick} aria-pressed={on} className={chipClass(on)}>
      {label} <span className="ml-1.5 tabular-nums opacity-70">{n}</span>
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chip("all", "All", total, category === "all", () => onCategory("all"))}
      {categories.map((f) => chip(f, fieldLabel(f), counts[f] ?? 0, category === f, () => onCategory(f)))}
      {onSide && (
        <span className="ml-2 flex items-center gap-1.5 border-l border-border pl-3">
          {(["all", "agent", "coder"] as const).map((s) => (
            <button key={s} type="button" onClick={() => onSide(s)} aria-pressed={side === s} className={chipClass(side === s)}>
              {s === "all" ? "All sides" : s === "agent" ? "Agent wrong" : "Coder wrong"}
            </button>
          ))}
        </span>
      )}
      <input
        type="search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        aria-label="Filter by code"
        placeholder="Code…"
        className="ml-auto h-7 w-32 rounded-coronis border border-border bg-card px-2 text-[12px] text-foreground"
      />
    </div>
  );
}
